<script>
// --- Destaque de caminhos de navegação "A › B › C" (breadcrumb) ---
// Conectores internos (Área DE Produtos) + hífen (BI - Inteligência): só entram
// no caminho quando flanqueados por palavras Cap dos dois lados.
const BC_FLANKED = ['de', 'da', 'do', 'das', 'dos', '-']
// Prosa que, mesmo capitalizada (início de frase), NÃO inicia um caminho —
// evita destacar "Acesse"/"Em"/"No" antes do path.
const BC_PROSE = new Set([
  'acesse', 'abra', 'em', 'no', 'na', 'use', 'va', 'selecione', 'clique',
  'veja', 'informe', 'escolha', 'defina', 'preencha', 'entre', 'confirme',
  'salve', 'localize', 'mantenha', 'cadastre', 'adicione', 'marque', 'arraste',
  'envie', 'complete', 'revise', 'reordene', 'remova', 'confira', 'monte',
  'crie', 'ligue', 'ajuste', 'permaneca', 'de', 'faca', 'nos', 'nas', 'para',
  'com', 'da', 'do',
])

const isSpaceTok = (t) => /^\s+$/.test(t)
const coreOf = (t) => t.replace(/^[^0-9A-Za-zÀ-ÿ]+|[^0-9A-Za-zÀ-ÿ]+$/g, '')

// Primeira letra maiúscula (inclui acentuadas). Dígitos não passam (coreOf +
// checagem de que a 1ª letra tem caixa distinta e está em maiúscula).
function isCapWord(word) {
  const core = coreOf(word)
  if (!core) return false
  const first = core.charAt(0)
  return first !== first.toLowerCase() && first === first.toUpperCase()
}

function normWord(t) {
  return coreOf(t).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

// Índice da palavra-vizinha (pula espaços) na direção dir (+1/-1).
function neighborWord(toks, i, dir) {
  let j = i + dir
  while (j >= 0 && j < toks.length && isSpaceTok(toks[j])) j += dir
  return j
}

// Marca cada token como elegível a compor um caminho: palavra Cap não-prosa,
// "›", espaço, ou conector/hífen flanqueado por Cap dos dois lados.
function markBreadcrumbEligible(toks) {
  const elig = toks.map(
    (t) => t === '›' || isSpaceTok(t) || (isCapWord(t) && !BC_PROSE.has(normWord(t)))
  )
  for (let i = 0; i < toks.length; i++) {
    if (elig[i] || toks[i] === '›' || isSpaceTok(toks[i])) continue
    if (BC_FLANKED.indexOf(toks[i].toLowerCase()) === -1) continue
    const p = neighborWord(toks, i, -1)
    const n = neighborWord(toks, i, +1)
    const pOk = p >= 0 && toks[p] !== '›' && isCapWord(toks[p])
    const nOk = n >= 0 && toks[n] !== '›' && isCapWord(toks[n])
    if (pOk && nOk) elig[i] = true
  }
  return elig
}

// Emite um run: destaca só se tem "›" E letra (não destaca "›" solto). Separa
// espaço/pontuação das bordas para não entrarem no destaque.
function emitBreadcrumbRun(out, run, hasArrow) {
  if (!(hasArrow && /[A-Za-zÀ-ÿ]/.test(run))) {
    out.push({ text: run, highlight: false })
    return
  }
  const lead = run.match(/^\s*/)[0]
  const trail = run.match(/[\s.,;:!?)]*$/)[0]
  const core = run.slice(lead.length, run.length - trail.length)
  if (lead) out.push({ text: lead, highlight: false })
  if (core) out.push({ text: core, highlight: true })
  if (trail) out.push({ text: trail, highlight: false })
}

// Fim do run elegível a partir de `start` + se contém "›".
function collectRun(toks, elig, start) {
  let j = start
  let hasArrow = false
  while (j < toks.length && elig[j]) {
    if (toks[j] === '›') hasArrow = true
    j += 1
  }
  return { end: j, hasArrow }
}

// Divide um trecho SEM aspas destacando os caminhos "A › B › C". Um run é a
// sequência maximal de tokens elegíveis; vira destaque quando contém "›".
function splitBreadcrumbs(out, text) {
  const toks = text.match(/›|\s+|[^\s›]+/g) || []
  const elig = markBreadcrumbEligible(toks)
  let i = 0
  while (i < toks.length) {
    if (!elig[i]) {
      out.push({ text: toks[i], highlight: false })
      i += 1
      continue
    }
    const run = collectRun(toks, elig, i)
    emitBreadcrumbRun(out, toks.slice(i, run.end).join(''), run.hasArrow)
    i = run.end
  }
}

export default {
  name: 'CentralConfigStepGuide',
  props: {
    // [{ title, detail }]
    instructions: { type: Array, default: () => [] },
  },
  computed: {
    // Sem instruções (payload vazio ou não-array) o card não deve renderizar —
    // evita mostrar um "Passo a passo" vazio.
    hasInstructions() {
      return Array.isArray(this.instructions) && this.instructions.length > 0
    },
    // Pré-computa os passos com o detalhe já quebrado em segmentos (trechos
    // entre aspas = rótulos de UI que o usuário precisa localizar/clicar, e
    // ganham destaque no template). Feito em computed para não chamar função
    // no template a cada re-render.
    steps() {
      if (!this.hasInstructions) return []
      return this.instructions.map((step) => ({
        title: step && step.title,
        segments: this.buildSegments(step && step.detail),
      }))
    },
  },
  methods: {
    // Alterna texto comum e trechos entre aspas (highlight: true), sem as aspas
    // — o destaque substitui os delimitadores. Nos trechos FORA de aspas, também
    // destaca caminhos de navegação "A › B › C" (breadcrumb). Sem v-html: cada
    // segmento vira um <span> no template.
    buildSegments(detail) {
      const text = detail || ''
      const segments = []
      const regex = /"([^"]+)"/g
      let lastIndex = 0
      let match = regex.exec(text)
      while (match !== null) {
        if (match.index > lastIndex) {
          this.pushPlainSegments(segments, text.slice(lastIndex, match.index))
        }
        segments.push({ text: match[1], highlight: true })
        lastIndex = regex.lastIndex
        match = regex.exec(text)
      }
      if (lastIndex < text.length) {
        this.pushPlainSegments(segments, text.slice(lastIndex))
      }
      return segments
    },
    // Empurra um trecho SEM aspas para `out`, destacando caminhos "A › B › C".
    pushPlainSegments(out, text) {
      if (!text) return
      if (text.indexOf('›') === -1) {
        out.push({ text, highlight: false })
        return
      }
      splitBreadcrumbs(out, text)
    },
  },
}
</script>

<template>
  <div v-if="hasInstructions" class="cc-guide">
    <span class="az-ds-card-title">Passo a passo</span>
    <ol class="cc-steps">
      <li
        v-for="(step, index) in steps"
        :key="index"
        class="cc-steps__item"
      >
        <span class="cc-steps__number">{{ index + 1 }}</span>
        <div class="cc-steps__content">
          <span class="az-ds-card-title cc-steps__title">{{ step.title }}</span>
          <span class="az-ds-aux cc-steps__detail"
            ><span
              v-for="(seg, sIndex) in step.segments"
              :key="sIndex"
              :class="{ 'cc-steps__hl': seg.highlight }"
              >{{ seg.text }}</span
            ></span
          >
        </div>
      </li>
    </ol>
  </div>
</template>

<style lang="sass" scoped>
.cc-guide
  padding: 0

.cc-steps
  list-style: none
  margin: 12px 0 0 0
  padding: 0

.cc-steps__item
  position: relative
  display: flex
  align-items: flex-start
  gap: 12px
  padding: 6px 0

// Linha vertical contínua conectando os números (timeline) — reforça a sequência
// e substitui os divisores horizontais (menos ruído, mais densidade).
.cc-steps__item:not(:last-child)::before
  content: ''
  position: absolute
  left: 11px
  top: 28px
  bottom: -6px
  width: 2px
  background: var(--border-hairline)

.cc-steps__number
  position: relative
  z-index: 1
  display: flex
  align-items: center
  justify-content: center
  width: 24px
  height: 24px
  border-radius: 50%
  background: var(--success-bg-soft)
  color: var(--primary)
  font-family: var(--font-family-paragraph)
  font-size: 12px
  font-weight: 600
  flex-shrink: 0

.cc-steps__content
  display: flex
  flex-direction: column
  gap: 3px
  min-width: 0

// Título do passo (tipografia via az-ds-card-title) — aqui só o line-height
// confortável para não grudar quando quebra em 2 linhas.
.cc-steps__title
  line-height: 1.35

// Detalhe do passo — auxiliar (az-ds-aux = 12/#8E8E8E) com line-height maior para
// leitura em múltiplas linhas.
.cc-steps__detail
  line-height: 1.45

// Rótulos de UI (trechos entre aspas nas instruções) — o usuário precisa
// localizá-los na tela para clicar. Ênfase pela via canônica do DS (peso +
// cor, como os modifiers az-text font-semibold): texto escuro (--neutral-900,
// o mesmo do az-ds-card-title) e semibold contra o cinza auxiliar do detalhe.
// Sem fundo/chip — cor semântica de fundo sairia do padrão e leria como status.
.cc-steps__hl
  font-weight: 600
  color: var(--neutral-900)
</style>
